// The facts no deal covers.
//
// Every other cost screen hangs off a deal, because a person asks what a
// customer costs. This one exists for the facts that have no deal to hang from:
// a tenant nothing is bound to is rated with a null deal_id, counted, and then
// invisible — its cost cannot reach a bill and nothing on a deal page will ever
// mention it (docs/crm-cost-explorer.md section 3).
//
// HOW IT FINDS THEM. The report groups by deployment, then by tenant inside each
// deployment, and the tenant bindings say which of those pairs a deal covers.
// The pair is the unit and not the tenant slug: a tenant slug is unique only
// inside one cluster, so `kingsport` on two deployments is two tenants, and a
// screen that grouped by slug alone would report one of them as bound because
// the other is.

import { useMemo, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useNavigate, useSearch } from "@tanstack/react-router";
import styles from "./UsagePage.module.css";
import { formatUsageMoney } from "./usageFormat.js";
import { mergeUsageCost, type UsageCostAnswer } from "./usageMerge.js";
import { isNonprod } from "./deploymentSelection.js";
import {
  buildProductMatrix,
  productLabel,
  type ProductMatrix,
  type ProductMatrixPart,
} from "./productMatrix.js";
import {
  periodToUsageSearch,
  searchToUsagePeriod,
  shiftUsagePeriod,
  type UsageSearch,
} from "./usageSearch.js";
import { Badge } from "../../design/Badge.js";
import { Card, CardHeader } from "../../design/Card.js";
import { EmptyState, Notice, ProblemMessage, Spinner } from "../../design/Feedback.js";
import { FilterSelect, MonthStepper } from "../../design/Filters.js";
import { StatTile, StatTileRow } from "../../design/StatTile.js";
import { Table, Td, Th } from "../../design/Table.js";
import { EmptyRow } from "../../design/Table.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { useAllTenantBindings, useUsageCost } from "../../api/queries.js";
import { formatMonth, todayIsoDate } from "../../format/calendarDate.js";
import { formatCount, formatPercent } from "../../format/money.js";
import { useReportCurrency } from "../../format/reportCurrency.js";

export function FleetUsagePage(): ReactNode {
  const search = useSearch({ from: "/usage" });
  const navigate = useNavigate();
  const today = todayIsoDate();
  const period = searchToUsagePeriod(search, today);

  usePageChrome({
    title: "Platform costs",
  });

  const bindings = useAllTenantBindings();

  // One fleet-wide read to learn which deployments have facts at all, then one
  // read per deployment for its tenants. The deployment list comes from the
  // ROLLUP rather than from a table of deployments, so one that pushed nothing
  // this month never appears.
  // Fleet-wide, across every deal, so in the reader's report currency.
  const currency = useReportCurrency();
  const fleet = useUsageCost([
    { period, groupBy: "deployment_slug", deploymentSlug: null, tenantSlug: null, currency },
  ]);
  const fleetUnion = mergeUsageCost(answersOf(fleet));
  const deployments = (fleetUnion.report?.groups ?? []).map((group) => group.key);

  const perDeployment = useUsageCost(
    deployments.map((deploymentSlug) => ({
      period,
      groupBy: "tenant_slug" as const,
      deploymentSlug,
      tenantSlug: null,
      currency,
    })),
  );

  const bound = useMemo(
    () =>
      new Set(
        (bindings.data?.items ?? []).map(
          (binding) => `${binding.deployment_slug} ${binding.tenant_slug}`,
        ),
      ),
    [bindings.data],
  );

  const rows = deployments.flatMap((deploymentSlug, index) => {
    const answer = perDeployment[index]?.data;
    const union = answer === undefined ? null : mergeUsageCost([answer]);
    return (union?.report?.groups ?? []).map((group) => ({
      deploymentSlug,
      tenantSlug: group.key,
      costMinor: group.cost_minor,
      currency: union?.report?.currency ?? "USD",
      bound: bound.has(`${deploymentSlug} ${group.key}`),
    }));
  });
  const unbound = rows
    .filter((row) => !row.bound)
    .sort((left, right) => right.costMinor - left.costMinor);

  // Each deployment by product: the same scopes as the tenant reads above, so
  // whatever refresh they start covers these too. Their sum is the fleet's
  // product split — the rollup rows behind two deployments are disjoint.
  const productByDeployment = useUsageCost(
    deployments.map((deploymentSlug) => ({
      period,
      groupBy: "product" as const,
      deploymentSlug,
      tenantSlug: null,
      currency,
    })),
  );
  const deploymentMatrix = buildProductMatrix(
    deployments.map((deploymentSlug, index) => ({
      key: deploymentSlug,
      report: reportOf(productByDeployment[index]?.data),
    })),
  );

  // One deployment's tenants at a time. A tenant-scoped read refreshes its own
  // scope, so splitting every tenant of the fleet at once would start a refresh
  // per tenant; one deployment is a handful, and a tenant slug only means
  // something inside its deployment anyway.
  const defaultTenantsOn = deployments.find((slug) => !isNonprod(slug)) ?? deployments[0] ?? null;
  const tenantsOn = search.tenantsOn ?? defaultTenantsOn;
  const tenantsOnDeployment = rows
    .filter((row) => row.deploymentSlug === tenantsOn)
    .map((row) => row.tenantSlug);
  const productByTenant = useUsageCost(
    tenantsOn === null
      ? []
      : tenantsOnDeployment.map((tenantSlug) => ({
          period,
          groupBy: "product" as const,
          deploymentSlug: tenantsOn,
          tenantSlug,
          currency,
        })),
  );
  const tenantMatrix = buildProductMatrix(
    tenantsOnDeployment.map((tenantSlug, index) => ({
      key: tenantSlug,
      report: reportOf(productByTenant[index]?.data),
    })),
  );

  const failed = fleet.find((result) => result.error !== null)?.error ?? null;
  const loading = fleet.some((result) => result.isPending) || bindings.isPending;

  const setTenantsOn = (next: string): void => {
    void navigate({
      to: "/usage",
      search: (previous: UsageSearch) => ({
        ...previous,
        tenantsOn: next === defaultTenantsOn ? undefined : next,
      }),
    });
  };

  const setPeriod = (next: string): void => {
    void navigate({
      to: "/usage",
      search: (previous: UsageSearch) => ({
        ...previous,
        period: periodToUsageSearch(next, today),
      }),
    });
  };

  return (
    <div className={styles.page}>
      <div className={styles.controls}>
        <MonthStepper
          label={formatMonth(period)}
          onStep={(delta) => setPeriod(shiftUsagePeriod(period, delta))}
        />
      </div>

      {failed !== null && <ProblemMessage error={failed} action="load the fleet's cost" />}

      {loading ? (
        <Spinner label="Loading the fleet's cost…" />
      ) : fleetUnion.report === null ? (
        <EmptyState
          title="No deployment has been rolled up for this month."
          description="The worker visits the open month every quarter of an hour. A deal's own screen is where a refresh is started."
        />
      ) : (
        <>
          <StatTileRow>
            <StatTile
              label="Fleet cost"
              value={formatUsageMoney(fleetUnion.report.cost_minor, fleetUnion.report.currency)}
              hint="every deployment, production and nonprod together"
            />
            <StatTile
              label="Deployments"
              value={formatCount(deployments.length)}
              hint="with a priced fact this month"
            />
            <StatTile
              label="Unbound tenants"
              value={formatCount(unbound.length)}
              hint="nothing they spend can reach a bill"
            />
          </StatTileRow>

          <Card>
            <CardHeader
              title="Cost by deployment"
              description="Nonprod is here and it is labelled. It costs real money and nobody can bill it, which is a reason to show it and never to add it to a billable total."
            />
            <Table label="Cost by deployment">
              <thead>
                <tr>
                  <Th>Deployment</Th>
                  <Th>Environment</Th>
                  <Th numeric>Cost</Th>
                </tr>
              </thead>
              <tbody>
                {fleetUnion.report.groups.map((group) => (
                  <tr key={group.key}>
                    <Td>{group.key}</Td>
                    <Td>
                      {isNonprod(group.key) ? (
                        <Badge tone="warn" shape="pill">
                          nonprod
                        </Badge>
                      ) : (
                        <Badge tone="neutral" shape="pill">
                          production
                        </Badge>
                      )}
                    </Td>
                    <Td numeric>
                      {formatUsageMoney(group.cost_minor, fleetUnion.report?.currency ?? "USD")}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>

          <Card>
            <CardHeader
              title="Cost by product"
              description="Every tenant on every deployment. A product with unpriced facts costs more than this says: those are facts the CRM has no rate for, not facts that were free."
            />
            <ProductTotalsTable matrix={deploymentMatrix} />
          </Card>

          <Card>
            <CardHeader
              title="Product cost by deployment"
              description="Each deployment's cost split by product. A dash is a product the deployment did not spend on."
            />
            <ProductMatrixTable
              label="Product cost by deployment"
              rowHeader="Deployment"
              matrix={deploymentMatrix}
              loading={productByDeployment.some((result) => result.isPending)}
            />
          </Card>

          <Card>
            <CardHeader
              title="Product cost by tenant"
              description="The tenants of one deployment, each split by product. A tenant slug is unique only inside its deployment, so one deployment at a time."
            />
            {tenantsOn !== null && (
              <div className={styles.controls}>
                <FilterSelect
                  label="Tenants on"
                  options={deployments.map((slug) => ({ value: slug, label: slug }))}
                  value={tenantsOn}
                  onChange={setTenantsOn}
                />
              </div>
            )}
            <ProductMatrixTable
              label="Product cost by tenant"
              rowHeader="Tenant"
              matrix={tenantMatrix}
              loading={productByTenant.some((result) => result.isPending)}
            />
          </Card>

          <Card>
            <CardHeader
              title="Tenants no deal covers"
              description="A tenant with no binding is still rated and still counted, with a null deal. Until somebody binds it, its cost belongs to nobody."
            />
            <Table label="Unbound tenants">
              <thead>
                <tr>
                  <Th>Tenant</Th>
                  <Th>Deployment</Th>
                  <Th numeric>Cost this month</Th>
                </tr>
              </thead>
              <tbody>
                {unbound.length === 0 ? (
                  <EmptyRow
                    colSpan={3}
                    title="Every tenant with facts this month is bound to a deal."
                    description="Nothing here is going unattributed."
                  />
                ) : (
                  unbound.map((row) => (
                    <tr key={`${row.deploymentSlug} ${row.tenantSlug}`}>
                      <Td>{row.tenantSlug}</Td>
                      <Td>{row.deploymentSlug}</Td>
                      <Td numeric>{formatUsageMoney(row.costMinor, row.currency)}</Td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card>

          <Notice tone="info" title="This screen says what the CRM can see, and no more.">
            {
              "Ingest lag, dropped facts and lapsed rates are the completeness report's answers, and it has its own screen in a later step. Binding a tenant to a deal is a write this screen does not carry: it is done on the tenant-bindings resource."
            }
            <div className={styles.footer}>
              <Link to="/deals">Deals →</Link>
            </div>
          </Notice>
        </>
      )}
    </div>
  );
}

/** What one read has to show, stale or fresh, or null while it has nothing. */
function reportOf(answer: UsageCostAnswer | undefined): ProductMatrixPart["report"] {
  return answer === undefined ? null : mergeUsageCost([answer]).report;
}

function unpricedCell(count: number): string {
  return count === 0 ? "—" : formatCount(count);
}

/** The fleet's cost per product, its share, and what no rate priced. */
function ProductTotalsTable({ matrix }: { readonly matrix: ProductMatrix }): ReactNode {
  const currency = matrix.currency ?? "USD";
  return (
    <Table label="Cost by product">
      <thead>
        <tr>
          <Th>Product</Th>
          <Th numeric>Cost</Th>
          <Th numeric>Share</Th>
          <Th numeric>Unpriced facts</Th>
        </tr>
      </thead>
      <tbody>
        {matrix.products.length === 0 ? (
          <EmptyRow
            colSpan={4}
            title="No product has a cost this month yet."
            description="The split appears once a deployment's figures load."
          />
        ) : (
          matrix.products.map((product) => {
            const cost = matrix.totals.get(product) ?? 0;
            return (
              <tr key={product}>
                <Td>{productLabel(product)}</Td>
                <Td numeric>{formatUsageMoney(cost, currency)}</Td>
                <Td numeric>
                  {matrix.totalMinor === 0
                    ? "—"
                    : formatPercent((cost / matrix.totalMinor) * 100, 0)}
                </Td>
                <Td numeric>{unpricedCell(matrix.unpricedByProduct.get(product) ?? 0)}</Td>
              </tr>
            );
          })
        )}
      </tbody>
    </Table>
  );
}

interface ProductMatrixTableProps {
  readonly label: string;
  /** The first column's heading: what a row is. */
  readonly rowHeader: string;
  readonly matrix: ProductMatrix;
  readonly loading: boolean;
}

/** One row per deployment or tenant, one column per product, then the row's total. */
function ProductMatrixTable({
  label,
  rowHeader,
  matrix,
  loading,
}: ProductMatrixTableProps): ReactNode {
  const currency = matrix.currency ?? "USD";
  const columns = matrix.products.length + 3;
  return (
    <Table label={label}>
      <thead>
        <tr>
          <Th>{rowHeader}</Th>
          {matrix.products.map((product) => (
            <Th key={product} numeric>
              {productLabel(product)}
            </Th>
          ))}
          <Th numeric>Total</Th>
          <Th numeric>Unpriced facts</Th>
        </tr>
      </thead>
      <tbody>
        {matrix.rows.length === 0 ? (
          <EmptyRow
            colSpan={columns}
            title={loading ? "Loading…" : "Nothing was spent here this month."}
            description={
              loading ? "Reading each one's cost by product." : "No priced or unpriced facts."
            }
          />
        ) : (
          matrix.rows.map((row) => (
            <tr key={row.key}>
              <Td>{row.key}</Td>
              {matrix.products.map((product) => {
                const cost = row.cells.get(product);
                return (
                  <Td key={product} numeric>
                    {cost === undefined ? "—" : formatUsageMoney(cost, currency)}
                  </Td>
                );
              })}
              <Td numeric>{formatUsageMoney(row.totalMinor, currency)}</Td>
              <Td numeric>{unpricedCell(row.unpricedFacts)}</Td>
            </tr>
          ))
        )}
      </tbody>
    </Table>
  );
}

function answersOf(results: readonly { data: UsageCostAnswer | undefined }[]): UsageCostAnswer[] {
  return results.flatMap((result) => (result.data === undefined ? [] : [result.data]));
}
